import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SfAccountSnapshot } from '@/types';
import { api } from '@/lib/api';
import { Loader2, Clock, AlertTriangle } from 'lucide-react';

interface CancelTrialModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account: SfAccountSnapshot;
  onSuccess: () => void;
  isChangingDate?: boolean;
}

function getTodayString(): string {
  return new Date().toISOString().split('T')[0];
}

function formatDateForDisplay(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function CancelTrialModal({
  open,
  onOpenChange,
  account,
  onSuccess,
  isChangingDate = false,
}: CancelTrialModalProps) {
  const today = getTodayString();
  const [cancelDate, setCancelDate] = useState(today);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const maxDate = account.ai_free_trial_end || undefined;

  const handleSubmit = async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      const result = isChangingDate
        ? await api.changeTrialCancellation(account.sf_account_id, cancelDate)
        : await api.cancelTrial(account.sf_account_id, cancelDate);

      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
          onSuccess();
        }, 2000);
      } else {
        setError(result.error || 'Failed to cancel trial');
      }
    } catch (err: unknown) {
      // Handle ApiError from the API client
      const apiError = err as { error?: string };
      const errorMessage = apiError?.error || (err instanceof Error ? err.message : 'An unexpected error occurred');
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setError(null);
      setSuccess(false);
      setCancelDate(getTodayString());
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-abby-primary" />
            {isChangingDate ? 'Change Cancellation Date' : 'Cancel Trial'}
          </DialogTitle>
          <DialogDescription>
            {isChangingDate
              ? 'Change the trial cancellation date. Service will continue until end of day on the selected date with no charges.'
              : 'Cancel the free trial for this account. Service will continue until end of day on the selected date with no charges.'}
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-6 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 mb-4">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm text-gray-600">
              {isChangingDate ? 'Cancellation date changed successfully!' : 'Trial cancelled successfully!'}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Service will remain active through end of day {formatDateForDisplay(cancelDate)}.
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="cancel-date">Cancellation Date</Label>
                <Input
                  id="cancel-date"
                  type="date"
                  value={cancelDate}
                  min={today}
                  max={maxDate}
                  onChange={(e) => setCancelDate(e.target.value)}
                  disabled={isSubmitting}
                />
                <p className="text-xs text-gray-500">
                  Service will remain active through end of day on this date.
                  {maxDate && (
                    <span className="block mt-1">
                      Trial end date: {formatDateForDisplay(maxDate)}
                    </span>
                  )}
                </p>
              </div>

              {cancelDate && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium text-amber-800">
                        {isChangingDate ? 'Confirm date change' : 'Confirm trial cancellation'}
                      </p>
                      <p className="text-amber-700 mt-1">
                        {isChangingDate
                          ? <>The cancellation date will be changed to <strong>{formatDateForDisplay(cancelDate)}</strong>. Service will remain active until end of day on that date.</>
                          : <>The trial will be cancelled effective end of day{' '}<strong>{formatDateForDisplay(cancelDate)}</strong>. No charges will apply.</>
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
                {isChangingDate ? 'Keep Current Date' : 'Keep Trial'}
              </Button>
              <Button onClick={handleSubmit} disabled={isSubmitting || !cancelDate}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {isChangingDate ? 'Changing...' : 'Cancelling...'}
                  </>
                ) : (
                  isChangingDate ? 'Change Date' : 'Cancel Trial'
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
